import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gradeService, Grade, GradeLaboratorio, GradeLaboratorioHorario } from '../services/api';
import LoadingModal from '../components/LoadingModal';
import ConfirmationModal from '../components/ConfirmationModal';
import jsPDF from 'jspdf';
import '../components/GradeGrid.css';

// Disciplina pode ter 1 ou 2 professores (coautoria) — combina os nomes pra exibição.
function professorNomeDaDisciplina(disciplina?: GradeLaboratorioHorario['disciplina'] | null): string {
  if (!disciplina) return '';
  const nomes = [disciplina.professor?.nome, disciplina.professor2?.nome].filter((nome): nome is string => !!nome);
  return nomes.join(' e ');
}

interface CellData {
  id: string;
  professor: string;
  disciplina: string;
  turma: string;
  gradeHorarioId: string;
  laboratorioId: string;
  horarioKey: string;
  dia: number;
  conflitoProfessorAgenda?: boolean;
  conflitoHorario?: boolean;
  originalData: GradeLaboratorioHorario;
}

interface LaboratorioData {
  laboratorioId: string;
  laboratorioNome: string;
  horarios: { [horarioKey: string]: GradeLaboratorioHorario[] };
}

const GradeLaboratorioViewPage: React.FC = () => {
  const { gradeId } = useParams<{ gradeId: string }>();
  const navigate = useNavigate();
  const [gradeLaboratorio, setGradeLaboratorio] = useState<GradeLaboratorio | null>(null);
  const [loading, setLoading] = useState(true);
  const [laboratoriosData, setLaboratoriosData] = useState<LaboratorioData[]>([]);
  const [draggedItem, setDraggedItem] = useState<CellData | null>(null);
  const [dragOverCell, setDragOverCell] = useState<{laboratorioId: string, dia: number, horarioKey: string} | null>(null);
  const [gridData, setGridData] = useState<LaboratorioData[]>([]);
  const [isModified, setIsModified] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPrintOptionsModal, setShowPrintOptionsModal] = useState(false);
  const [laboratoriosPorFolha, setLaboratoriosPorFolha] = useState(1);
  const [professorDestacado, setProfessorDestacado] = useState<string | null>(null);
  // Grade principal (não a de laboratório) — usada pra saber em quais horários o professor
  // de fato tem aula com aquela turma/disciplina, já que o card só pode estar num horário
  // onde isso é verdade (o professor precisa estar com a turma pra poder ir ao laboratório).
  const [gradeBase, setGradeBase] = useState<Grade | null>(null);
  const [cardParaMover, setCardParaMover] = useState<CellData | null>(null);

  const diasSemana = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];

  const chaveCardBase = (disciplinaId: string, turmaId: string) => `${disciplinaId}:${turmaId}`;
  const chaveHorario = (diaSemana: number, inicio: string, fim: string) => `${diaSemana}-${inicio}-${fim}`;

  // Pra cada (disciplina, turma), todos os horários em que isso de fato acontece na grade
  // principal — é exatamente onde um card desse par pode estar na grade de laboratório,
  // já que o professor só pode levar a turma ao laboratório num horário em que está com ela.
  const horariosValidosPorCard = useMemo(() => {
    const mapa = new Map<string, Set<string>>();
    gradeBase?.horarios?.forEach((gh) => {
      if (!gh.disciplina || !gh.turma) return;
      const chave = chaveCardBase(gh.disciplina.id, gh.turma.id);
      if (!mapa.has(chave)) mapa.set(chave, new Set());
      mapa.get(chave)!.add(chaveHorario(gh.horario.diaSemana, gh.horario.inicio, gh.horario.fim));
    });
    return mapa;
  }, [gradeBase]);

  // Se não houver grade principal carregada, não bloqueia (degrada pro comportamento
  // anterior) — só valida quando temos como saber a verdade.
  const horarioEhValidoParaCard = (item: CellData, dia: number, horarioTexto: string): boolean => {
    const disciplinaId = item.originalData.disciplina?.id;
    const turmaId = item.originalData.turma?.id;
    if (!disciplinaId || !turmaId) return true;
    const validos = horariosValidosPorCard.get(chaveCardBase(disciplinaId, turmaId));
    if (!validos) return !gradeBase; // sem grade base carregada, não dá pra confirmar nem negar
    return validos.has(chaveHorario(dia, ...(horarioTexto.split('-') as [string, string])));
  };

  // Função para verificar conflitos via API
  const verificarConflitos = async (gradeHorario: GradeLaboratorioHorario) => {
    if (!gradeLaboratorio || !gradeHorario.disciplina) {
      return false;
    }

    try {
      const temConflito = await gradeService.checkCards(
        gradeLaboratorio.id,
        gradeHorario.disciplina.id,
        gradeHorario.horario.id
      );
      return !temConflito; // Se a API retorna true (sem conflito), retornamos false para conflito
    } catch (error) {
      console.error('Erro ao verificar conflitos:', error);
      return false; // Em caso de erro, assumimos que não há conflito
    }
  };

  const loadGradeLaboratorio = useCallback(async () => {
    try {
      setLoading(true);
      const data = await gradeService.getGradeLaboratorioById(gradeId!);
      setGradeLaboratorio(data);

      // Carrega a grade principal em paralelo — é dela que vem a verdade sobre em quais
      // horários cada professor de fato está com cada turma.
      try {
        const base = await gradeService.getById(data.gradeId);
        setGradeBase(base);
      } catch (error) {
        console.error('Erro ao carregar a grade principal:', error);
        setGradeBase(null);
      }

      // Organizar dados por laboratório
      const laboratoriosMap = new Map<string, LaboratorioData>();

      data.gradeHorarios.forEach((horario) => {
        const labId = horario.laboratorio.id;
        const labNome = horario.laboratorio.nome;
        
        if (!laboratoriosMap.has(labId)) {
          laboratoriosMap.set(labId, {
            laboratorioId: labId,
            laboratorioNome: labNome,
            horarios: {}
          });
        }

        const lab = laboratoriosMap.get(labId)!;
        const horarioKey = `${horario.horario.diaSemana}-${horario.horario.inicio}-${horario.horario.fim}`;
        
        if (!lab.horarios[horarioKey]) {
          lab.horarios[horarioKey] = [];
        }
        
        lab.horarios[horarioKey].push(horario);
      });
      
      const laboratoriosArray = Array.from(laboratoriosMap.values());

      setLaboratoriosData(laboratoriosArray);
      setGridData(laboratoriosArray);
      setIsModified(false);

    } catch (error) {
      console.error('Erro ao carregar grade de laboratório:', error);
    } finally {
      setLoading(false);
    }
  }, [gradeId]);

  useEffect(() => {
    if (gradeId) {
      loadGradeLaboratorio();
    }
  }, [gradeId, loadGradeLaboratorio]);

  const handlePrintLaboratorios = async (laboratoriosPorFolha: number) => {
    setShowPrintOptionsModal(false);
    
    try {
      // Configurar PDF em formato A4 retrato
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth(); // 210mm
      const pageHeight = pdf.internal.pageSize.getHeight(); // 297mm
      const margin = 15;
      const contentWidth = pageWidth - (2 * margin);
      
      let currentPage = 0;
      
      // Dividir laboratórios em grupos baseado na quantidade por folha
      for (let i = 0; i < gridData.length; i += laboratoriosPorFolha) {
        if (currentPage > 0) {
          pdf.addPage();
        }
        currentPage++;
        
        const labsThisPage = gridData.slice(i, i + laboratoriosPorFolha);
        let currentY = margin;
        
        // Título da página - COM acentuação correta
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        const titulo = `Horários de Laboratório - Informática`;
        pdf.text(titulo, pageWidth / 2, currentY, { align: 'center' });
        currentY += 15;
        
        // Data de geração
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        const dataAtual = new Date().toLocaleDateString('pt-BR');
        pdf.text(`Gerado em: ${dataAtual}`, pageWidth / 2, currentY, { align: 'center' });
        currentY += 20;
        
        // Calcular altura disponível para cada laboratório
        const availableHeight = pageHeight - currentY - margin;
        const labHeight = availableHeight / laboratoriosPorFolha;
        
        // Processar cada laboratório desta página
        for (let j = 0; j < labsThisPage.length; j++) {
          const laboratorio = labsThisPage[j];
          const labStartY = currentY + (j * labHeight);
          
          // Nome do laboratório - COM acentuação
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(40, 116, 166); // Azul
          pdf.text(`Laboratório: ${laboratorio.laboratorioNome}`, margin, labStartY);
          
          // Criar tabela de horários
          const tableStartY = labStartY + 10;
          const cellHeight = 15; // Aumentado para acomodar fonte maior
          const timeColWidth = 30;
          const dayColWidth = (contentWidth - timeColWidth) / 5;
          
          // Cabeçalho da tabela - CORRIGIDO
          pdf.setFontSize(12); // Equivalente a Arial 12
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0); // Preto
          pdf.setFillColor(220, 220, 220); // Cinza claro
          pdf.setDrawColor(0, 0, 0); // Borda preta
          
          // Célula de horário
          pdf.rect(margin, tableStartY, timeColWidth, cellHeight, 'FD');
          pdf.text('Horário', margin + timeColWidth/2, tableStartY + 9, { align: 'center' });
          
          // Células dos dias da semana - CORRIGIDAS
          diasSemana.forEach((dia, index) => {
            const x = margin + timeColWidth + (index * dayColWidth);
            pdf.setFillColor(220, 220, 220); // Cinza claro
            pdf.setTextColor(0, 0, 0); // Texto preto
            pdf.rect(x, tableStartY, dayColWidth, cellHeight, 'FD');
            pdf.text(dia, x + dayColWidth/2, tableStartY + 9, { align: 'center' });
          });
          
          // Obter horários únicos para este laboratório
          const horariosUnicos = getHorariosComIds().filter(horarioInfo => {
            return Object.keys(laboratorio.horarios).some(key => {
              const [, inicio, fim] = key.split('-');
              return `${inicio}-${fim}` === horarioInfo.texto;
            });
          });
          
          // Linhas da tabela
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10); // Tamanho equivalente a Arial 12 para conteúdo
          
          horariosUnicos.forEach((horarioInfo, rowIndex) => {
            const rowY = tableStartY + cellHeight + (rowIndex * cellHeight);
            
            // Célula do horário - SEM negrito
            pdf.setFillColor(248, 248, 248); // Cinza muito claro
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'normal'); // Fonte normal, sem negrito
            pdf.rect(margin, rowY, timeColWidth, cellHeight, 'FD');
            pdf.text(horarioInfo.texto, margin + timeColWidth/2, rowY + 9, { align: 'center' });
            
            // Células dos dias
            for (let dia = 1; dia <= 5; dia++) {
              const x = margin + timeColWidth + ((dia - 1) * dayColWidth);
              const item = getCellItemFromGrid(laboratorio.laboratorioId, dia, horarioInfo.texto);
              
              if (item && item.disciplina && item.turma && item.professor) {
                // Célula ocupada
                pdf.setFillColor(240, 248, 255); // Azul muito claro
                pdf.rect(x, rowY, dayColWidth, cellHeight, 'FD');
                
                // Disciplina (linha 1)
                pdf.setTextColor(0, 0, 139); // Azul escuro
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(9);
                const disciplina = item.disciplina.length > 12 ? item.disciplina.substring(0, 12) + '...' : item.disciplina;
                pdf.text(disciplina, x + dayColWidth/2, rowY + 4, { align: 'center' });
                
                // Turma (linha 2)
                pdf.setTextColor(0, 0, 0); // Preto
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(8);
                const turma = item.turma.length > 10 ? item.turma.substring(0, 10) + '...' : item.turma;
                pdf.text(turma, x + dayColWidth/2, rowY + 8, { align: 'center' });
                
                // Professor (linha 3)
                pdf.setTextColor(0, 0, 0); // Preto para melhor visibilidade
                pdf.setFont('helvetica', 'bold'); // Negrito para destaque
                pdf.setFontSize(10); // Tamanho equivalente a Arial 12
                const professor = item.professor.length > 12 ? item.professor.substring(0, 12) + '...' : item.professor;
                pdf.text(professor.toUpperCase(), x + dayColWidth/2, rowY + 12, { align: 'center' });
              } else {
                // Célula livre
                pdf.setFillColor(250, 250, 250); // Branco
                pdf.rect(x, rowY, dayColWidth, cellHeight, 'FD');
                pdf.setTextColor(150, 150, 150); // Cinza claro
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(8);
                pdf.text('LIVRE', x + dayColWidth/2, rowY + 9, { align: 'center' });
              }
            }
          });
          
          // Linha separadora entre laboratórios (se não for o último)
          if (j < labsThisPage.length - 1) {
            const separatorY = labStartY + labHeight - 5;
            pdf.setDrawColor(200, 200, 200);
            pdf.line(margin, separatorY, pageWidth - margin, separatorY);
          }
        }
      }
      
      // Abrir PDF em nova guia
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      setErrorMessage('Erro ao gerar PDF. Tente novamente.');
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  // Nova função para obter horários únicos com seus IDs, ordenados por posicaoDia
  const getHorariosComIds = () => {
    const horariosMap = new Map<string, { id: string, posicaoDia?: number }>(); // texto -> {id, posicaoDia}
    const horariosTexto = new Set<string>();
    
    gridData.forEach(lab => {
      Object.values(lab.horarios).forEach(horarios => {
        horarios.forEach(h => {
          const textoHorario = `${h.horario.inicio}-${h.horario.fim}`;
          horariosTexto.add(textoHorario);
          if (!horariosMap.has(textoHorario)) {
            horariosMap.set(textoHorario, { 
              id: h.horario.id,
              posicaoDia: (h.horario as any).posicaoDia // Usar posicaoDia se disponível
            });
          }
        });
      });
    });
    
    return Array.from(horariosTexto).map(texto => ({
      texto,
      id: horariosMap.get(texto)!.id,
      posicaoDia: horariosMap.get(texto)!.posicaoDia
    })).sort((a, b) => {
      // Ordenar por posicaoDia se disponível, senão por texto
      if (a.posicaoDia !== undefined && b.posicaoDia !== undefined) {
        return a.posicaoDia - b.posicaoDia;
      }
      return a.texto.localeCompare(b.texto);
    });
  };

  // Funções de Drag and Drop
  const handleDragStart = (e: React.DragEvent, item: CellData) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, laboratorioId: string, dia: number, horarioKey: string) => {
    e.preventDefault();
    
    // Permitir drag over para qualquer laboratório
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell({ laboratorioId, dia, horarioKey });
  };

  const handleDragLeave = () => {
    setDragOverCell(null);
  };

  // Reagrupa gradeHorarios por laboratório/horário — mesma forma usada em loadGradeLaboratorio.
  const reagruparGrid = (gradeHorarios: GradeLaboratorioHorario[]): LaboratorioData[] => {
    const laboratoriosMap = new Map<string, LaboratorioData>();
    gradeHorarios.forEach((horario) => {
      const labId = horario.laboratorio.id;
      const labNome = horario.laboratorio.nome;
      if (!laboratoriosMap.has(labId)) {
        laboratoriosMap.set(labId, { laboratorioId: labId, laboratorioNome: labNome, horarios: {} });
      }
      const lab = laboratoriosMap.get(labId)!;
      const horarioKey = `${horario.horario.diaSemana}-${horario.horario.inicio}-${horario.horario.fim}`;
      if (!lab.horarios[horarioKey]) lab.horarios[horarioKey] = [];
      lab.horarios[horarioKey].push(horario);
    });
    return Array.from(laboratoriosMap.values());
  };

  const mostrarErroTemporario = (mensagem: string) => {
    setErrorMessage(mensagem);
    setTimeout(() => setErrorMessage(null), 5000);
  };

  const handleDrop = (e: React.DragEvent, targetLaboratorioId: string, targetDia: number, targetHorarioTexto: string) => {
    e.preventDefault();
    setDragOverCell(null);

    if (!draggedItem || !gradeLaboratorio) {
      return;
    }

    // Soltando na própria célula de origem — nada a fazer.
    if (draggedItem.laboratorioId === targetLaboratorioId &&
        draggedItem.dia === targetDia &&
        `${draggedItem.originalData.horario.inicio}-${draggedItem.originalData.horario.fim}` === targetHorarioTexto) {
      setDraggedItem(null);
      return;
    }

    const draggedGradeHorario = gradeLaboratorio.gradeHorarios.find(gh => gh.id === draggedItem.gradeHorarioId);
    if (!draggedGradeHorario) {
      setDraggedItem(null);
      return;
    }

    // Só existe um GradeLaboratorioHorario no destino se alguma aula já estiver alocada
    // ali — uma célula "livre" não tem registro nenhum (não é um registro com dados nulos).
    const targetGradeHorario = gradeLaboratorio.gradeHorarios.find(gh =>
      gh.laboratorio.id === targetLaboratorioId &&
      gh.horario.diaSemana === targetDia &&
      `${gh.horario.inicio}-${gh.horario.fim}` === targetHorarioTexto
    );

    const labDestino = gridData.find(l => l.laboratorioId === targetLaboratorioId);
    if (!labDestino) {
      setDraggedItem(null);
      return;
    }

    // Pra mover pra uma célula livre preciso do Horario real (id) — a Grade principal é a
    // fonte mais completa (a de Laboratório só guarda aulas que pediram laboratório), mas
    // qualquer uma serve: a movimentação manual nunca é bloqueada por isso, só avisada
    // (ver abaixo) — então não exijo que a combinação bata com a disciplina/turma aqui.
    const horarioReferencia = targetGradeHorario?.horario
      ?? gradeBase?.horarios?.find(gh =>
        gh.horario.diaSemana === targetDia && `${gh.horario.inicio}-${gh.horario.fim}` === targetHorarioTexto
      )?.horario
      ?? gradeLaboratorio.gradeHorarios.find(gh =>
        gh.horario.diaSemana === targetDia && `${gh.horario.inicio}-${gh.horario.fim}` === targetHorarioTexto
      )?.horario;

    if (!horarioReferencia) {
      mostrarErroTemporario('Não foi possível identificar esse horário de destino.');
      setDraggedItem(null);
      return;
    }

    // Aviso (nunca bloqueia): o professor não tem essa disciplina com essa turma nesse
    // horário, segundo a grade principal — o card fica marcado em vermelho (getCardClasses)
    // e o usuário decide se mantém assim.
    const avisos: string[] = [];
    if (!horarioEhValidoParaCard(draggedItem, targetDia, targetHorarioTexto)) {
      avisos.push(
        `${draggedItem.professor} não tem aula de ${draggedItem.disciplina} com a turma ${draggedItem.turma} nesse horário, segundo a grade principal.`
      );
    }

    if (targetGradeHorario) {
      const horarioOrigemTexto = `${draggedGradeHorario.horario.inicio}-${draggedGradeHorario.horario.fim}`;
      const targetItem: CellData = {
        id: targetGradeHorario.id,
        professor: professorNomeDaDisciplina(targetGradeHorario.disciplina),
        disciplina: targetGradeHorario.disciplina?.nome || '',
        turma: targetGradeHorario.turma?.nome || '',
        gradeHorarioId: targetGradeHorario.id,
        laboratorioId: targetGradeHorario.laboratorio.id,
        horarioKey: `${targetGradeHorario.horario.diaSemana}-${targetGradeHorario.horario.inicio}-${targetGradeHorario.horario.fim}`,
        dia: targetGradeHorario.horario.diaSemana,
        originalData: targetGradeHorario,
      };
      if (!horarioEhValidoParaCard(targetItem, draggedGradeHorario.horario.diaSemana, horarioOrigemTexto)) {
        avisos.push(
          `${targetItem.professor} não tem aula de ${targetItem.disciplina} com a turma ${targetItem.turma} no horário de origem, segundo a grade principal.`
        );
      }

      // Troca entre dois cards com dados — troca disciplina, turma e propriedades de conflito.
      const tempDisciplina = draggedGradeHorario.disciplina;
      const tempTurma = draggedGradeHorario.turma;
      const tempConflitoProfessorAgenda = draggedGradeHorario.conflitoProfessorAgenda;
      const tempConflitoHorario = draggedGradeHorario.conflitoHorario;

      draggedGradeHorario.disciplina = targetGradeHorario.disciplina;
      draggedGradeHorario.turma = targetGradeHorario.turma;
      draggedGradeHorario.conflitoProfessorAgenda = targetGradeHorario.conflitoProfessorAgenda;
      draggedGradeHorario.conflitoHorario = targetGradeHorario.conflitoHorario;
      draggedGradeHorario.alterado = true;

      targetGradeHorario.disciplina = tempDisciplina;
      targetGradeHorario.turma = tempTurma;
      targetGradeHorario.conflitoProfessorAgenda = tempConflitoProfessorAgenda;
      targetGradeHorario.conflitoHorario = tempConflitoHorario;
      targetGradeHorario.alterado = true;
    } else {
      // Célula livre (sem registro) — move o PRÓPRIO registro arrastado pra lá, em vez de
      // procurar um destino pra "preencher" (não existe nenhum).
      draggedGradeHorario.horario = horarioReferencia;
      draggedGradeHorario.laboratorio = { id: targetLaboratorioId, nome: labDestino.laboratorioNome };
      draggedGradeHorario.alterado = true;
    }

    if (avisos.length > 0) {
      mostrarErroTemporario(avisos.join(' '));
    }

    setGradeLaboratorio({ ...gradeLaboratorio });
    setGridData(reagruparGrid(gradeLaboratorio.gradeHorarios));
    setIsModified(true);
    setCardParaMover(null); // qualquer movimento limpa o destaque verde de destinos válidos

    // Verifica conflito de agenda do laboratório pros registros que mudaram de posição.
    const verificarConflitosAsync = async () => {
      if (draggedGradeHorario.disciplina) {
        draggedGradeHorario.conflitoProfessorAgenda = await verificarConflitos(draggedGradeHorario);
      }
      if (targetGradeHorario?.disciplina) {
        targetGradeHorario.conflitoProfessorAgenda = await verificarConflitos(targetGradeHorario);
      }
      setGradeLaboratorio({ ...gradeLaboratorio });
      setGridData(reagruparGrid(gradeLaboratorio.gradeHorarios));
    };

    verificarConflitosAsync();
    setDraggedItem(null);
  };

  // Função auxiliar para obter item de uma célula específica do grid
  const getCellItemFromGrid = (laboratorioId: string, dia: number, horarioTexto: string): CellData | null => {
    // Buscar diretamente nos gradeHorarios originais
    if (!gradeLaboratorio) return null;
    
    const gradeHorario = gradeLaboratorio.gradeHorarios.find(gh => 
      gh.laboratorio.id === laboratorioId &&
      gh.horario.diaSemana === dia &&
      `${gh.horario.inicio}-${gh.horario.fim}` === horarioTexto &&
      gh.disciplina && gh.disciplina.professor && gh.turma // Tem dados preenchidos
    );
    
    if (gradeHorario) {
      return {
        id: gradeHorario.id,
        professor: professorNomeDaDisciplina(gradeHorario.disciplina),
        disciplina: gradeHorario.disciplina?.nome || '',
        turma: gradeHorario.turma?.nome || '',
        gradeHorarioId: gradeHorario.id,
        laboratorioId: gradeHorario.laboratorio.id,
        horarioKey: `${gradeHorario.horario.diaSemana}-${gradeHorario.horario.inicio}-${gradeHorario.horario.fim}`,
        dia: gradeHorario.horario.diaSemana,
        conflitoProfessorAgenda: gradeHorario.conflitoProfessorAgenda,
        conflitoHorario: gradeHorario.conflitoHorario,
        originalData: gradeHorario
      };
    }
    
    return null;
  };

  // Função para destacar professor (em todas as suas aulas) e, separadamente, marcar pra
  // onde este card específico pode ser movido (horários livres em verde, ver getCardClasses
  // e a renderização das células vazias).
  const handleDoubleClick = (item: CellData) => {
    if (professorDestacado === item.professor) {
      setProfessorDestacado(null); // Remove destaque se já estiver destacado
    } else {
      setProfessorDestacado(item.professor); // Destaca o professor
    }

    setCardParaMover((atual) => (atual?.id === item.id ? null : item));
  };

  // O card não corresponde a uma aula real do professor com essa turma nesse horário, na
  // grade principal — usado tanto pro destaque vermelho quanto pro tooltip explicativo.
  const cardForaDaGradeBase = (item: CellData): boolean => {
    const texto = `${item.originalData.horario.inicio}-${item.originalData.horario.fim}`;
    return !horarioEhValidoParaCard(item, item.dia, texto);
  };

  // Função para obter classes CSS do card
  const getCardClasses = (item: CellData) => {
    let classes = '';

    // Não bate com a grade principal tem prioridade sobre tudo — é o problema mais sério.
    if (cardForaDaGradeBase(item)) {
      classes += ' fora-da-grade-base';
    } else if (item.conflitoProfessorAgenda && item.conflitoHorario) {
      classes += ' both-conflicts';
    } else if (item.conflitoProfessorAgenda) {
      classes += ' professor-agenda-conflict';
    } else if (item.conflitoHorario) {
      classes += ' schedule-conflict';
    } else if (professorDestacado && item.professor === professorDestacado) {
      // Só aplica destaque se não houver conflitos
      classes += ' professor-highlighted';
    }

    return classes;
  };

  // Função para salvar alterações
  const handleSave = () => {
    setShowConfirmationModal(true);
  };

  const performSave = async () => {
    try {
      setSaving(true);
      setShowConfirmationModal(false);

      // Coletar todos os itens modificados
      const modifiedItems: any[] = [];

      gridData.forEach(laboratorio => {
        Object.values(laboratorio.horarios).forEach(horarios => {
          horarios.forEach(horario => {
            // Verificar se foi marcado como alterado via drag and drop
            if (horario.alterado) {
              modifiedItems.push({
                id: horario.id,
                turmaId: horario.turma?.id || '',
                disciplinaId: horario.disciplina?.id || '',
                horarioId: horario.horario?.id || '',
                laboratorioId: horario.laboratorio?.id || '',
                professorId: horario.disciplina?.professor?.id || '',
                conflitoProfessorAgenda: horario.conflitoProfessorAgenda || false,
                conflitoHorario: horario.conflitoHorario || false,
                bloqueado: horario.bloqueado || false
              });
              return; // Pular a verificação de comparação se já foi marcado como alterado
            }

            // Encontrar o item original correspondente para outras alterações
            const originalLab = laboratoriosData.find(lab =>
              Object.values(lab.horarios).some(originalHorarios =>
                originalHorarios.some(orig => orig.id === horario.id)
              )
            );

            if (originalLab) {
              const originalHorario = Object.values(originalLab.horarios)
                .flat()
                .find(orig => orig.id === horario.id);

              // Verificar se houve mudanças (incluindo mudanças de horário)
              if (originalHorario && (
                originalHorario.disciplina?.id !== horario.disciplina?.id ||
                originalHorario.turma?.id !== horario.turma?.id ||
                originalHorario.laboratorio?.id !== horario.laboratorio?.id ||
                originalHorario.disciplina?.professor?.id !== horario.disciplina?.professor?.id ||
                originalHorario.horario?.diaSemana !== horario.horario?.diaSemana ||
                originalHorario.horario?.inicio !== horario.horario?.inicio ||
                originalHorario.horario?.fim !== horario.horario?.fim
              )) {
                modifiedItems.push({
                  id: horario.id,
                  turmaId: horario.turma?.id || '',
                  disciplinaId: horario.disciplina?.id || '',
                  horarioId: horario.horario?.id || '',
                  laboratorioId: horario.laboratorio?.id || '',
                  professorId: horario.disciplina?.professor?.id || '',
                  conflitoProfessorAgenda: horario.conflitoProfessorAgenda || false,
                  conflitoHorario: horario.conflitoHorario || false,
                  bloqueado: horario.bloqueado || false
                });
              }
            }
          });
        });
      });

      if (modifiedItems.length === 0) {
        setSuccessMessage('Nenhuma alteração foi detectada.');
        setTimeout(() => setSuccessMessage(null), 3000);
        return;
      }

      // Fazer a requisição PUT
      await gradeService.updateGradeLaboratorio(modifiedItems);
      
      setSuccessMessage('Alterações salvas com sucesso!');
      setIsModified(false);
      
      // Recarregar os dados
      await loadGradeLaboratorio();
      
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (error) {
      console.error('Erro ao salvar alterações:', error);
      setErrorMessage('Erro ao salvar alterações. Tente novamente.');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  // Loading state - seguindo o padrão da GradeViewPage
  if (loading) {
    return (
      <div className="container-fluid py-4" style={{ marginTop: '60px' }}>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="mt-3 text-muted">Carregando grade de laboratório...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!gradeLaboratorio) {
    return (
      <div className="container-fluid py-4" style={{ marginTop: '60px' }}>
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Grade não encontrada.
        </div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left me-2"></i>
          Voltar
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4" style={{ marginTop: '60px' }}>
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div className="flex-grow-1">
              <h2 className="mb-2 text-start">
                <i className="fas fa-flask me-2 text-success"></i>
                {gradeLaboratorio?.nome || 'Grade de Laboratório'}
                {isModified && (
                  <span className="badge bg-warning text-dark ms-2">
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    Lembre-se de salvar
                  </span>
                )}
              </h2>
              {gradeLaboratorio.data && (
                <small className="text-muted">
                  <i className="fas fa-calendar me-2"></i>
                  {new Date(gradeLaboratorio.data).toLocaleDateString('pt-BR')}
                </small>
              )}
            </div>
            <div className="d-flex gap-2">
              <button 
                className="btn btn-outline-primary"
                onClick={() => setShowPrintOptionsModal(true)}
                title="Gerar PDF dos horários de laboratório para impressão"
              >
                <i className="fas fa-print me-2"></i>
                Imprimir
              </button>
              {isModified && (
                <button
                  className="btn btn-success"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Salvar Alterações
                    </>
                  )}
                </button>
              )}
              <button
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Voltar
              </button>
            </div>
          </div>

          {/* Grades por Laboratório */}
          {/* Grade de Laboratórios */}
          {gridData.length === 0 ? (
            <div className="alert alert-info">
              <h4>Nenhum horário encontrado</h4>
              <p>Esta grade de laboratório não possui horários definidos.</p>
            </div>
          ) : (
            gridData
              .sort((a, b) => a.laboratorioNome.localeCompare(b.laboratorioNome))
              .map((laboratorio) => (
                <div key={laboratorio.laboratorioId} className="mb-5">
                  <h4 className="mb-3">
                    <i className="fas fa-flask me-2 text-success"></i>
                    {laboratorio.laboratorioNome}
                  </h4>
                  
                  <div className="day-grid">
                    <div className="day-header-row">
                      <div className="time-header">Horário</div>
                      {diasSemana.map((dia, index) => (
                        <div key={index} className="day-header">{dia}</div>
                      ))}
                      <div className="phantom-column"></div>
                    </div>
                    
                    {getHorariosComIds().map((horarioInfo) => (
                      <div key={horarioInfo.texto} className="time-row">
                        <div className="time-cell">{horarioInfo.texto}</div>
                        {[1, 2, 3, 4, 5].map((dia) => {
                          const item = getCellItemFromGrid(laboratorio.laboratorioId, dia, horarioInfo.texto);
                          const isDragOver = dragOverCell?.laboratorioId === laboratorio.laboratorioId &&
                                           dragOverCell?.dia === dia &&
                                           dragOverCell?.horarioKey === horarioInfo.texto;
                          const ehDestinoValido = !item && cardParaMover && horarioEhValidoParaCard(cardParaMover, dia, horarioInfo.texto);

                          return (
                            <div
                              key={dia}
                              className={`grid-cell ${
                                item ? 'occupied' : 'empty'
                              } ${
                                isDragOver && (!draggedItem || draggedItem.laboratorioId === laboratorio.laboratorioId) ? 'drag-over' : ''
                              }${item ? getCardClasses(item) : ''}${ehDestinoValido ? ' valid-move-target' : ''}`}
                              draggable={!!item}
                              onDragStart={item ? (e) => handleDragStart(e, item) : undefined}
                              onDragOver={(e) => handleDragOver(e, laboratorio.laboratorioId, dia, horarioInfo.texto)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, laboratorio.laboratorioId, dia, horarioInfo.texto)}
                              style={item ? { cursor: 'move' } : {}}
                            >
                              {item ? (
                                <div
                                  className="cell-content"
                                  onDoubleClick={() => handleDoubleClick(item)}
                                  title={
                                    (cardForaDaGradeBase(item)
                                      ? `${item.professor} não tem aula de ${item.disciplina} com a turma ${item.turma} nesse horário, segundo a grade principal.\n\n`
                                      : '') + `Duplo clique para destacar/remover destaque do professor ${item.professor}`
                                  }
                                >
                                  <div className="cell-professor">{item.professor || 'Professor não informado'}</div>
                                  <div className="cell-disciplina">{item.disciplina || 'Disciplina não informada'}</div>
                                  <div className="cell-turma">{item.turma || 'Turma não informada'}</div>
                                </div>
                              ) : (
                                <div className="cell-empty">LIVRE</div>
                              )}
                            </div>
                          );
                        })}
                        <div className="grid-cell phantom-column"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

      {/* Modais */}
      <LoadingModal isOpen={saving} title="Salvando" message="Salvando alterações..." />
      
      <ConfirmationModal
        isOpen={showConfirmationModal}
        title="Salvar Alterações"
        message="Deseja salvar as alterações feitas na grade de laboratório?"
        onConfirm={performSave}
        onCancel={() => setShowConfirmationModal(false)}
      />

      {/* Modal de Opções de Impressão */}
      {showPrintOptionsModal && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title d-flex align-items-center">
                    <i className="fas fa-print me-2"></i>
                    Opções de Impressão - Laboratórios
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    onClick={() => setShowPrintOptionsModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-4">
                    <h6 className="fw-bold text-primary mb-3">
                      <i className="fas fa-list me-2"></i>
                      Quantos laboratórios por folha?
                    </h6>
                    <div className="row g-3 justify-content-center">
                      <div className="col-6">
                        <div 
                          className={`card border-0 shadow-sm h-100 ${laboratoriosPorFolha === 1 ? 'border-primary' : ''}`} 
                          style={{ cursor: 'pointer' }} 
                          onClick={() => {
                            setLaboratoriosPorFolha(1);
                            handlePrintLaboratorios(1);
                          }}
                        >
                          <div className="card-body p-4 text-center">
                            <i className="fas fa-file-alt text-primary mb-3" style={{ fontSize: '2rem' }}></i>
                            <h5 className="card-title text-primary mb-2">1 por folha</h5>
                            <p className="card-text text-muted">Máxima qualidade</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div 
                          className={`card border-0 shadow-sm h-100 ${laboratoriosPorFolha === 2 ? 'border-primary' : ''}`} 
                          style={{ cursor: 'pointer' }} 
                          onClick={() => {
                            setLaboratoriosPorFolha(2);
                            handlePrintLaboratorios(2);
                          }}
                        >
                          <div className="card-body p-4 text-center">
                            <i className="fas fa-copy text-success mb-3" style={{ fontSize: '2rem' }}></i>
                            <h5 className="card-title text-success mb-2">2 por folha</h5>
                            <p className="card-text text-muted">Boa qualidade</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary d-flex align-items-center" 
                    onClick={() => setShowPrintOptionsModal(false)}
                  >
                    <i className="fas fa-times me-2"></i>
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* Mensagens de Feedback */}
      {successMessage && (
        <div className="position-fixed top-0 start-50 translate-middle-x mt-3" style={{ zIndex: 1050 }}>
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {successMessage}
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="position-fixed top-0 start-50 translate-middle-x mt-3" style={{ zIndex: 1050 }}>
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-circle me-2"></i>
            {errorMessage}
          </div>
        </div>
      )}
    </div>
  );
};

export default GradeLaboratorioViewPage;